
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { trackAddToCart, trackPurchase, trackViewItem } from '../services/attribution';
import { sectionLabel } from '../constants/sections';
import useSWR from 'swr';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { User, Product, ProductVariant, SiteConfig, CompanySettings, ClassGroup } from '../types';
import { db } from '../services/db';
import type { CheckoutDelivery, OrderReceipt } from '../services/db/orders';
import type { CheckoutFields } from './store/CheckoutForm';
import { clearPendingCheckout, loadStripe, readPendingCheckout, restoreAfterStripeReturn, savePendingCheckout, stripeReturnParams } from '../services/stripeClient';
import { useAuth } from '../context/AuthContext';
import { ThemeManager } from './landing/ThemeManager';
import { useToast } from './ui/Toast';
import { StoreSkeleton } from './ui/Skeleton'; 
import { DEFAULT_COMPANY_SETTINGS } from '../services/db/cms';
import { SEOHead } from './SEOHead';
import { Edit2 } from 'lucide-react';

// Components
import { StoreHeader } from './store/StoreHeader';
import { StoreHero } from './store/StoreHero';
import { ShopCatalog, CategoryNav } from './store/ShopCatalog';
import { SupportBar } from './store/CampaignCountdown';
import { CartDrawer } from './store/CartDrawer';
import { OrderSuccess } from './store/OrderSuccess';
import { StoreProductPage } from './store/StoreProductPage';
import { ContentSection } from './landing/sections/ContentSection';
import { FeaturesSection } from './landing/sections/FeaturesSection';
import { SiteFooter } from './landing/SiteFooter';
import { ProductShowcase } from './landing/sections/ProductShowcase';
import { ShopContentSection } from './store/sections/ShopContentSection';
import { BundleChoices, bundleItemsFor, bundleValue, choicesComplete, choicesKey, isBundle, resolveBundle, visibleForClass } from '../services/bundles';
import { buildShopSections, cardSubtitle, effectiveStock, isPurchasable } from '../services/shop';

interface StoreFrontProps {
  student?: User;
  schoolClass?: ClassGroup; 
  previewConfig?: SiteConfig;
  previewCompany?: CompanySettings; 
  onSectionClick?: (id: string) => void;
  activeSectionId?: string | null;
  scrollContainerId?: string;
}

type CartLine = { product: Product; variant?: ProductVariant; quantity: number; choices?: BundleChoices };

/** Samma produkt, variant och val i paketet = samma rad i varukorgen. */
const sameLine = (a: CartLine, product: Product, variant?: ProductVariant, choices?: BundleChoices) =>
  a.product.id === product.id && a.variant?.id === variant?.id && choicesKey(a.choices) === choicesKey(choices);

// Varukorgen sparas lokalt så den överlever en siduppdatering eller att
// mobilen byter app (t.ex. till Swish/SMS). Vi sparar bara id:n och antal —
// namn och priser läses alltid om från aktuella produkter.
const CART_TTL_MS = 7 * 24 * 60 * 60 * 1000;
// En varukorg per säljare, så att en kund som besöker flera säljares butiker
// inte får med sig fel varor till fel säljare.
const cartStorageKey = (sellerId?: string) => `cn_cart_${sellerId || 'store'}`;

type StoredCart = { savedAt: number; lines: { productId: string; variantId?: string; quantity: number; choices?: BundleChoices }[] };

const readStoredCart = (key: string): StoredCart['lines'] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredCart;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > CART_TTL_MS) {
      localStorage.removeItem(key);
      return [];
    }
    return Array.isArray(parsed.lines) ? parsed.lines : [];
  } catch {
    return [];
  }
};

const writeStoredCart = (key: string, cart: CartLine[]) => {
  try {
    if (cart.length === 0) {
      localStorage.removeItem(key);
      return;
    }
    const payload: StoredCart = {
      savedAt: Date.now(),
      lines: cart.map(l => ({ productId: l.product.id, variantId: l.variant?.id, quantity: l.quantity, ...(l.choices ? { choices: l.choices } : {}) }))
    };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // Privat läge/full lagring: varukorgen fungerar ändå, den sparas bara inte.
  }
};

const MAX_QTY_PER_LINE = 100;

/** "?choose=produkt:variant,…" från länken till ett paket, t.ex. samma färg som på Flaskan. */
const parseChoicesParam = (raw: string | null): BundleChoices | undefined => {
  if (!raw) return undefined;
  const pairs = raw.split(',').map(p => p.split(':')).filter(p => p.length === 2 && /^[A-Za-z0-9-]{1,64}$/.test(p[0]) && /^[A-Za-z0-9-]{1,64}$/.test(p[1]));
  return pairs.length > 0 ? Object.fromEntries(pairs) : undefined;
};

export const StoreFront: React.FC<StoreFrontProps> = ({ student: initialStudent, schoolClass: initialClass, previewConfig, previewCompany, onSectionClick, activeSectionId, scrollContainerId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Ref för scoping av temat i preview-läge
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const activeProductId = searchParams.get('product');
  const isCheckoutOpen = searchParams.get('cart') === 'open';

  const [cart, setCart] = useState<CartLine[]>([]);
  const [receipt, setReceipt] = useState<OrderReceipt | null>(null);
  const [orderComplete, setOrderComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Hemleverans: ordern finns men väntar på betalning i Stripes fält.
  const [pendingPayment, setPendingPayment] = useState<OrderReceipt | null>(null);

  // OPTIMIZATION: Instant load config from cache
  const cachedConfig = useMemo(() => {
      try {
          const raw = localStorage.getItem('cn_config_store_page');
          return raw ? JSON.parse(raw) : undefined;
      } catch (e) { return undefined; }
  }, []);

  const { data: fetchedConfig, isLoading: configLoading } = useSWR(
      !previewConfig ? 'site-config-store' : null, 
      () => db.getSiteConfig('store_page'),
      { 
        revalidateOnFocus: false,
        fallbackData: cachedConfig
      }
  );

  const { data: fetchedCompany } = useSWR(
      !previewCompany ? 'company-settings' : null,
      () => db.getCompanySettings(),
      { fallbackData: DEFAULT_COMPANY_SETTINGS }
  );

  // OPTIMIZATION: Instant load products from cache
  const localProducts = useMemo(() => {
      try {
          const raw = localStorage.getItem('cn_products_cache');
          return raw ? JSON.parse(raw) : undefined;
      } catch (e) { return undefined; }
  }, []);

  const { data: cachedProducts, isLoading: productsLoading } = useSWR(
      'public-products',
      async () => {
          const data = await db.getProducts();
          // Update cache in background
          try { localStorage.setItem('cn_products_cache', JSON.stringify(data)); } catch(e){}
          return data;
      },
      { 
        revalidateOnFocus: false,
        fallbackData: localProducts
      }
  );

  // Kategorierna ger butikens sektioner och deras ordning.
  const { data: categories, isLoading: categoriesLoading } = useSWR('product-categories', () => db.getCategories(), { revalidateOnFocus: false });

  const config = previewConfig || fetchedConfig;
  const company = previewCompany || fetchedCompany || DEFAULT_COMPANY_SETTINGS;
  const [student, setStudent] = useState<User | undefined>(initialStudent);
  const [schoolClass, setSchoolClass] = useState<any | null>(initialClass); 

  // Alla produkter, för paketens innehåll.
  const catalog: Product[] = useMemo(() => cachedProducts || [], [cachedProducts]);
  // Bara produkter med kategori säljs (kategorin bestämmer momsen), och paket (momsen kommer
  // från delarna). Produkter som bara visas för vissa grupper syns bara i deras butiker.
  // Databasen stoppar resten.
  const products = useMemo(() => catalog
      .filter((p: Product) => (!!p.categoryId || isBundle(p)) && visibleForClass(p, schoolClass?.id))
      .map((p: Product) => ({ ...p, shopSubtitle: cardSubtitle(p, catalog), shopStock: effectiveStock(p, catalog) }))
      .map((p: Product) => {
          if (!isBundle(p)) return p;
          const variant = p.variants?.find(v => (v.bundleItems?.length ?? 0) > 0);
          const parts = resolveBundle(bundleItemsFor(p, variant), catalog, []);
          const price = variant ? variant.price : p.price;
          const savings = Math.round((bundleValue(parts) - price) * 100) / 100;
          const ownImage = (p.images?.length ?? 0) > 0 || !!p.imageUrl;
          // Samma produkt flera gånger i paketet ger bara en bild.
          const partImages = [...new Set(parts.map(x => x.imageUrl).filter((u): u is string => !!u))];
          // Utan egen bild visas delarnas bilder, en i taget.
          return { ...p, bundleSavings: savings > 0 ? savings : 0, bundleImages: ownImage ? undefined : partImages, images: ownImage ? p.images : partImages };
      }), [catalog, schoolClass?.id]);

  useEffect(() => {
      if (initialStudent?.uniqueSlug && !initialClass) {
          db.getStoreData(initialStudent.uniqueSlug).then(data => {
              if (data) {
                  setStudent(data.student);
                  setSchoolClass(data.classInfo);
              }
          });
      } else {
          setStudent(initialStudent);
          setSchoolClass(initialClass);
      }
  }, [initialStudent, initialClass]);

  const shopSections = useMemo(() => buildShopSections(products, categories || []), [products, categories]);

  const activeProduct = useMemo(() => products.find(p => p.id === activeProductId), [products, activeProductId]);

  // Återställ sparad varukorg när produkterna finns (en gång per butik).
  const cartKey = cartStorageKey(initialStudent?.id);
  const restoredCartRef = useRef(false);
  useEffect(() => {
      if (previewConfig || restoredCartRef.current || products.length === 0) return;
      restoredCartRef.current = true;
      const lines = readStoredCart(cartKey);
      if (lines.length === 0) return;
      const restored: CartLine[] = [];
      lines.forEach(line => {
          const product = products.find((p: Product) => p.id === line.productId);
          if (!product) return;
          const variant = line.variantId ? product.variants?.find((v: ProductVariant) => v.id === line.variantId) : undefined;
          if (line.variantId && !variant) return;
          // Slut i lager sedan varukorgen sparades (eller en del i paketet): databasen skulle neka ordern.
          if (!isPurchasable(product, catalog)) return;
          // Paket: valen måste finnas kvar (t.ex. en färg som tagits bort).
          const choices = line.choices && typeof line.choices === 'object' ? line.choices : undefined;
          if (isBundle(product) && !choicesComplete(bundleItemsFor(product, variant), catalog, choices)) return;
          const quantity = Math.min(Math.max(1, Math.floor(line.quantity) || 1), MAX_QTY_PER_LINE);
          restored.push({ product, variant, quantity, ...(choices ? { choices } : {}) });
      });
      if (restored.length > 0) setCart(restored);
  }, [products, cartKey, previewConfig]);

  useEffect(() => {
      if (previewConfig || !restoredCartRef.current) return;
      writeStoredCart(cartKey, cart);
  }, [cart, cartKey, previewConfig]);

  useEffect(() => {
      if (activeProduct) {
          const target = scrollContainerId ? document.getElementById(scrollContainerId) : window;
          target?.scrollTo({ top: 0, behavior: 'instant' });
      }
  }, [activeProduct, scrollContainerId]);

  // Statistik (bara efter samtycke): produktsidan öppnas.
  useEffect(() => {
      if (!activeProduct || previewConfig) return;
      trackViewItem({ item_id: activeProduct.id, item_name: activeProduct.name, price: activeProduct.price, quantity: 1 });
      // Egen statistik per produkt: utan cookies, inget om besökaren.
      if (student?.uniqueSlug) db.recordProductEvent(student.uniqueSlug, activeProduct.id, 'view');
  }, [activeProduct?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addToCart = (product: Product, variant?: ProductVariant, openDrawer: boolean = true, choices?: BundleChoices) => {
    if (previewConfig) return;
    trackAddToCart({ item_id: product.id, item_name: product.name, item_variant: variant?.name, price: variant ? variant.price : product.price, quantity: 1 });
    if (student?.uniqueSlug) db.recordProductEvent(student.uniqueSlug, product.id, 'cart');

    // Ingen mutation av befintliga rader (React kan köra uppdateraren två gånger).
    setCart(prev => {
      const idx = prev.findIndex(item => sameLine(item, product, variant, choices));
      if (idx >= 0) {
          return prev.map((item, i) => i === idx ? { ...item, quantity: Math.min(item.quantity + 1, MAX_QTY_PER_LINE) } : item);
      }
      return [...prev, { product, variant, quantity: 1, ...(choices ? { choices } : {}) }];
    });
    if (openDrawer) setSearchParams(prev => { prev.set('cart', 'open'); return prev; });
  };

  const finishOrder = (result: OrderReceipt) => {
    setReceipt(result);
    trackPurchase({ ...result, totalAmount: result.totalAmount === null ? null : result.totalAmount + (result.shippingAmount || 0) });
    setOrderComplete(true);
    setPendingPayment(null);
    setCart([]);
    writeStoredCart(cartKey, []);
  };

  // Tillbaka från Swish/Klarna/3D Secure: kontrollera betalningen hos Stripe
  // och visa tacksidan. Ordern markeras betald av webhooken, inte här.
  useEffect(() => {
    const params = stripeReturnParams();
    if (!params || previewConfig) return;
    const pending = readPendingCheckout<OrderReceipt>();
    restoreAfterStripeReturn(pending?.hash || window.location.hash);
    if (!pending) return;
    loadStripe()
      .then(stripe => stripe.retrievePaymentIntent(params.clientSecret))
      .then(({ paymentIntent }) => {
        const status = paymentIntent?.status;
        if (status === 'succeeded' || status === 'processing') {
          clearPendingCheckout();
          finishOrder({ ...pending.receipt, paymentState: status });
        } else {
          // Avbrutet eller nekat: tillbaka till betalfältet med samma order.
          setPendingPayment(pending.receipt);
          setSearchParams(prev => { prev.set('cart', 'open'); return prev; });
          toast('Betalningen blev inte klar. Försök igen eller välj ett annat betalsätt.', 'error');
        }
      })
      .catch(() => toast('Vi kunde inte kontrollera betalningen. Du får ett mejl när den är klar.', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitOrder = async (form: CheckoutFields, studentCode: string, offerDeclined: boolean, delivery: CheckoutDelivery) => {
    setIsSubmitting(true);
    try {
        let sid = student?.id;
        let sname = student?.name;
        if (!student && studentCode) {
            const data = await db.getStoreData(studentCode.trim().toLowerCase());
            if (data) { sid = data.student.id; sname = data.student.name; }
            else throw new Error("Säljkod hittades inte.");
        }
        if (!sid) throw new Error("Ingen säljare vald.");

        const result = await db.createOrder({
          id: self.crypto.randomUUID(),
          studentId: sid,
          studentName: sname,
          customerName: form.name,
          customerEmail: form.email,
          customerAddress: delivery.method === 'HOME' ? form.address : '',
          customerPhone: form.phone,
          totalAmount: cart.reduce((s, i) => s + (i.variant ? i.variant.price : i.product.price) * i.quantity, 0),
          status: 'PENDING',
          paymentStatus: 'UNPAID',
          timestamp: Date.now(),
          items: cart.map(c => ({ 
              productId: c.product.id, 
              variantId: c.variant?.id,
              variantName: c.variant?.name,
              ...(c.choices ? { choices: c.choices } : {}),
              quantity: c.quantity, 
              priceAtMoment: c.variant ? c.variant.price : c.product.price 
          })),
          // Erbjudandemejlet efter kampanjen gäller alla som inte tackat nej.
          marketingConsent: !offerDeclined
        }, delivery, { offerDeclined });
        const withCustomer: OrderReceipt = {
          ...result,
          customerFirstName: form.name.trim().split(' ')[0],
          shippingAddress: delivery.method === 'HOME' ? `${form.address.trim()}, ${form.postalCode.trim()} ${form.city.trim()}` : undefined,
          campaignEnd: schoolClass?.campaignEnd,
          className: schoolClass?.name,
        };
        if (withCustomer.clientSecret) {
          // Hemleverans: nästa steg är betalningen i kassan.
          setPendingPayment(withCustomer);
        } else {
          finishOrder(withCustomer);
        }
    } catch (e: any) {
        toast(e.message, 'error');
    } finally {
        setIsSubmitting(false);
    }
  };

  if (!config && configLoading) return <StoreSkeleton />;
  if (!config) return <div className="min-h-screen bg-background" />;
  
  if (orderComplete) return <OrderSuccess receipt={receipt} onReset={() => { setOrderComplete(false); setReceipt(null); setSearchParams({}); }} />;

  const cartItemCount = cart.reduce((sum, line) => sum + line.quantity, 0);
  const sections = config?.sections || [];
  const hasExplicitGrid = sections.some(s => s.type === 'PRODUCT_GRID');
  const seoTitle = student ? `Köp av ${student.name} | CoffeeNova` : 'Butik | CoffeeNova';
  const shopTop = (
    <>
      <SupportBar groupName={schoolClass?.name} sellerName={student?.name} campaignEnd={schoolClass?.campaignEnd} />
      <CategoryNav sections={shopSections} scrollContainerId={scrollContainerId} />
    </>
  );
  const catalogBlock = (
    <ShopCatalog
      sections={shopSections}
      onSelect={p => !previewConfig && setSearchParams({ product: p.id })}
      cardStyle={config.theme.productCardStyle}
      isLoading={(productsLoading || categoriesLoading) && shopSections.length === 0}
    />
  );

  return (
    <div 
        ref={containerRef}
        className="min-h-screen bg-background font-sans flex flex-col font-theme-store" 
        id={scrollContainerId || "main-scroll-container"}
    >
      {!activeProduct && <SEOHead title={seoTitle} />}
      
      {/* Om previewConfig finns (Edit Mode), skicka med ref för att scopa stilar */}
      {config && <ThemeManager theme={config.theme} rootRef={previewConfig ? containerRef : undefined} />}
      
      {!activeProduct && (
        <StoreHeader 
            scrollContainerId={scrollContainerId}
            student={student} 
            className={schoolClass?.name}
            cartCount={cartItemCount} 
            onOpenCart={() => !previewConfig && setSearchParams({cart: 'open'})} 
            theme={config?.theme} 
            isPreview={!!previewConfig}
        />
      )}

      <div className="flex-1">
        {activeProduct ? (
            <StoreProductPage 
                product={activeProduct} 
                onBack={() => setSearchParams({})} 
                onAddToCart={addToCart} 
                cart={cart} 
                onOpenCart={() => setSearchParams({cart:'open'})} 
                allProducts={products} 
                catalog={catalog}
                theme={config?.theme} 
                campaignEnd={schoolClass?.campaignEnd}
                onSelectRelated={(p, variantId, choices) => !previewConfig && setSearchParams({ product: p.id, ...(variantId ? { variant: variantId } : {}), ...(choices ? { choose: choicesKey(choices) } : {}) })}
                initialVariantId={searchParams.get('variant')}
                initialChoices={parseChoicesParam(searchParams.get('choose'))}
            />
        ) : (
            <>
                {/* Utan hero: plats för det fasta sidhuvudet. */}
                {sections[0]?.type !== 'HERO' && <div className="h-16" aria-hidden="true" />}
                {sections[0]?.type !== 'HERO' && shopTop}
                {sections.map((s, i) => {
                    const isEditable = !!onSectionClick;
                    const commonProps = {
                        key: s.id,
                        id: s.id,
                        onClick: (e: any) => {
                            if (onSectionClick) {
                                e.stopPropagation();
                                onSectionClick(s.id);
                            }
                        },
                        className: `relative transition-all duration-200 ${onSectionClick ? 'cursor-pointer hover:ring-2 hover:ring-coffee-400 hover:ring-inset hover:z-10' : ''} ${activeSectionId === s.id ? 'ring-4 ring-coffee-600 ring-inset z-20 shadow-2xl' : ''}`
                    };
                    
                    const renderContent = () => {
                        switch (s.type) {
                            case 'HERO': 
                                return (
                                    <StoreHero 
                                        student={student}
                                        className={schoolClass?.name} 
                                        title={s.title} 
                                        text={s.text} 
                                        imageUrl={s.imageUrl}
                                        mobileImageUrl={s.mobileImageUrl}
                                        theme={config?.theme}
                                        heroLayout={s.heroLayout} 
                                        titleColor={s.heroTitleColor}
                                        textColor={s.heroTextColor}
                                        titleShadow={s.heroTitleShadow}
                                        textShadow={s.heroTextShadow}
                                        shadow={s.heroShadow}
                                        titleSize={s.heroTitleSize}
                                        subtitleSize={s.heroSubtitleSize}
                                        height={s.heroHeight}
                                        heroTitleTexture={s.heroTitleTexture}
                                        heroTitleOutlineWidth={s.heroTitleOutlineWidth}
                                        heroTitleOutlineColor={s.heroTitleOutlineColor}
                                        heroTextOutlineWidth={s.heroTextOutlineWidth}
                                        heroTextOutlineColor={s.heroTextOutlineColor}
                                        heroTextAlignment={s.heroTextAlignment}
                                        heroContentPosition={s.heroContentPosition}
                                        heroContentPositionY={s.heroContentPositionY}
                                        heroButtonColor={s.heroButtonColor}
                                        heroButtonTextColor={s.heroButtonTextColor}
                                        scrollContainerId={scrollContainerId} 
                                        heroVideoUrl={s.heroVideoUrl}
                                    />
                                );
                            case 'FEATURES': return <FeaturesSection section={s} />;
                            case 'CONTENT': return <ContentSection section={s} />;
                            case 'PRODUCTS': return (
                                <ProductShowcase
                                    section={s}
                                    theme={config.theme}
                                    products={products}
                                    classId={schoolClass?.id}
                                    onSelect={p => !previewConfig && setSearchParams({ product: p.id })}
                                />
                            );
                            case 'PRODUCT_GRID':
                                return catalogBlock;
                            case 'REVIEWS':
                            case 'MEDIA': return <ShopContentSection section={s} editing={isEditable} />;
                            default: return null;
                        }
                    };

                    return (
                        <React.Fragment key={s.id}>
                        <div {...commonProps}>
                            {isEditable && (
                                <>
                                    <div className={`absolute top-4 left-1/2 -translate-x-1/2 bg-coffee-600 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-lg flex items-center gap-2 transition-opacity z-50 pointer-events-none ${
                                        activeSectionId === s.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                    }`}>
                                        <Edit2 size={10} /> Redigerar: {sectionLabel(s.type)}
                                    </div>
                                    <div className="absolute inset-0 z-40 cursor-pointer" />
                                </>
                            )}
                            <div className={isEditable ? 'pointer-events-none' : ''}>
                                {renderContent()}
                            </div>
                        </div>
                        {/* Direkt under heron: vem kunden stödjer, nedräkningen och kategoriraden. */}
                        {i === 0 && s.type === 'HERO' && shopTop}
                        </React.Fragment>
                    );
                })}
                
                {!hasExplicitGrid && catalogBlock}
            </>
        )}
      </div>
      {config && <SiteFooter config={config} company={company} onAction={() => !previewConfig && navigate('/')} />}
      <CartDrawer isOpen={isCheckoutOpen} onClose={() => setSearchParams({})} cart={cart} catalog={catalog} onRemove={line => setCart(prev => prev.filter(i => !sameLine(i, line.product, line.variant, line.choices)))} onUpdateQuantity={(line, d) => setCart(prev => prev.map(i => sameLine(i, line.product, line.variant, line.choices) ? {...i, quantity: Math.min(MAX_QTY_PER_LINE, Math.max(1, i.quantity + d))} : i))} cartTotal={cart.reduce((s, i) => s + (i.variant ? i.variant.price : i.product.price) * i.quantity, 0)} cartCount={cartItemCount} student={student} onSubmitOrder={handleSubmitOrder} isSubmitting={isSubmitting} isCampaignEnded={schoolClass?.campaignEnd ? schoolClass.campaignEnd < Date.now() : false} campaignEnd={schoolClass?.campaignEnd}
        paymentOptions={schoolClass?.paymentOptions}
        pendingPayment={pendingPayment}
        onPaid={status => { clearPendingCheckout(); if (pendingPayment) finishOrder({ ...pendingPayment, paymentState: status }); }}
        onBeforeRedirect={() => { if (pendingPayment) savePendingCheckout({ ...pendingPayment, clientSecret: undefined }); }}
        onCancelPayment={() => setPendingPayment(null)} />
    </div>
  );
};
